<template>
  <div class="ec2-codex-page">
    <div class="page-header">
      <div>
        <div class="text-overline">Fulladmin</div>
        <h1>EC2 Codex</h1>
        <p>Controle da maquina Windows usada para Codex remoto.</p>
      </div>

      <div class="header-actions">
        <v-btn outlined color="primary" :loading="loadingStatus" @click="loadStatus">
          <v-icon left>mdi-refresh</v-icon>
          Atualizar
        </v-btn>
      </div>
    </div>

    <v-alert v-if="error" type="error" outlined dense class="mb-4">
      {{ error }}
    </v-alert>

    <v-row>
      <v-col cols="12" md="4">
        <v-card outlined class="fill-height">
          <v-card-title class="panel-title">
            <v-icon left>mdi-server</v-icon>
            Estado
          </v-card-title>
          <v-card-text>
            <div class="state-row">
              <v-chip :color="stateColor" dark>{{ status.state || "desconhecido" }}</v-chip>
              <span class="caption grey--text text--darken-1">{{ status.instanceType || "-" }}</span>
            </div>

            <div class="detail-list mt-4">
              <div>
                <span>Instancia</span>
                <strong>{{ status.instanceId || "-" }}</strong>
              </div>
              <div>
                <span>Regiao</span>
                <strong>{{ status.region || "-" }}</strong>
              </div>
              <div>
                <span>Status EC2</span>
                <strong>{{ status.instanceStatus || "-" }}</strong>
              </div>
              <div>
                <span>Status sistema</span>
                <strong>{{ status.systemStatus || "-" }}</strong>
              </div>
              <div>
                <span>Ligada desde</span>
                <strong>{{ formatDate(status.launchTime) }}</strong>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="8">
        <v-card outlined class="fill-height">
          <v-card-title class="panel-title">
            <v-icon left>mdi-remote-desktop</v-icon>
            Conexao RDP
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <div class="detail-list">
                  <div>
                    <span>Host atual</span>
                    <strong>{{ status.rdpHost || "-" }}</strong>
                  </div>
                  <div>
                    <span>IP publico</span>
                    <strong>{{ status.publicIpAddress || "-" }}</strong>
                  </div>
                  <div>
                    <span>DNS publico</span>
                    <strong>{{ status.publicDnsName || "-" }}</strong>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="detail-list">
                  <div>
                    <span>IP privado</span>
                    <strong>{{ status.privateIpAddress || "-" }}</strong>
                  </div>
                  <div>
                    <span>Porta RDP</span>
                    <strong>{{ status.rdpPort || 3389 }}</strong>
                  </div>
                  <div>
                    <span>Usuario</span>
                    <strong>{{ status.rdpUsername || "Administrator" }}</strong>
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-alert v-if="lastAllowedCidr" type="info" outlined dense class="mt-4 mb-0">
              RDP liberado para {{ lastAllowedCidr }}.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined class="mt-4">
      <v-card-title class="panel-title">
        <v-icon left>mdi-console</v-icon>
        Acoes
      </v-card-title>
      <v-card-text>
        <div class="action-grid">
          <v-btn color="success" :loading="loadingStart" :disabled="isRunningLike" @click="startInstance">
            <v-icon left>mdi-play</v-icon>
            Ligar maquina
          </v-btn>
          <v-btn color="error" outlined :loading="loadingStop" :disabled="isStoppedLike" @click="stopInstance">
            <v-icon left>mdi-stop</v-icon>
            Desligar maquina
          </v-btn>
          <v-btn color="primary" outlined :loading="loadingAllowRdp" :disabled="!status.canAuthorizeRdp" @click="allowRdp">
            <v-icon left>mdi-shield-key</v-icon>
            Liberar meu IP
          </v-btn>
          <v-btn color="primary" :loading="loadingRdp" :disabled="!status.rdpAvailable" @click="downloadRdp">
            <v-icon left>mdi-download</v-icon>
            Baixar RDP
          </v-btn>
          <v-btn outlined :disabled="!status.rdpHost" @click="copyHost">
            <v-icon left>mdi-content-copy</v-icon>
            Copiar host
          </v-btn>
        </div>

        <v-progress-linear v-if="isTransitioning" indeterminate color="primary" class="mt-4" />

        <div class="help-text mt-4">
          <p>Fluxo recomendado: ligar a maquina, aguardar o estado <strong>running</strong>, liberar seu IP e baixar o RDP atualizado.</p>
          <p>O RDP baixado usa o IP/DNS atual da AWS. Nenhuma senha da EC2 e nenhuma credencial AWS fica no navegador.</p>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { ec2CodexAPI } from "../API/Ec2CodexAPI";

export default {
  name: "Ec2CodexPanel",
  data() {
    return {
      status: {},
      error: "",
      lastAllowedCidr: "",
      pollTimer: null,
      loadingStatus: false,
      loadingStart: false,
      loadingStop: false,
      loadingAllowRdp: false,
      loadingRdp: false,
    };
  },
  computed: {
    normalizedState() {
      return String(this.status.state || "").toLowerCase();
    },
    isRunningLike() {
      return ["pending", "running"].includes(this.normalizedState);
    },
    isStoppedLike() {
      return ["stopping", "stopped", "shutting-down", "terminated"].includes(this.normalizedState);
    },
    isTransitioning() {
      return ["pending", "stopping", "shutting-down"].includes(this.normalizedState);
    },
    stateColor() {
      const colors = {
        running: "success",
        pending: "warning",
        stopping: "warning",
        stopped: "grey",
        terminated: "error",
      };

      return colors[this.normalizedState] || "primary";
    },
  },
  mounted() {
    this.loadStatus();
    this.pollTimer = window.setInterval(() => {
      if (this.isTransitioning || this.normalizedState === "running") {
        this.loadStatus(true);
      }
    }, 10000);
  },
  beforeDestroy() {
    if (this.pollTimer) {
      window.clearInterval(this.pollTimer);
    }
  },
  methods: {
    async loadStatus(silent = false) {
      this.error = "";
      this.loadingStatus = !silent;

      try {
        const response = await ec2CodexAPI.status();
        this.status = response.data || {};
      } catch (error) {
        this.error = this.extractError(error);
      } finally {
        this.loadingStatus = false;
      }
    },
    async startInstance() {
      this.error = "";
      this.loadingStart = true;

      try {
        const response = await ec2CodexAPI.start();
        this.status = response.data || {};
      } catch (error) {
        this.error = this.extractError(error);
      } finally {
        this.loadingStart = false;
      }
    },
    async stopInstance() {
      this.error = "";
      this.loadingStop = true;

      try {
        const response = await ec2CodexAPI.stop();
        this.status = response.data || {};
      } catch (error) {
        this.error = this.extractError(error);
      } finally {
        this.loadingStop = false;
      }
    },
    async allowRdp() {
      this.error = "";
      this.loadingAllowRdp = true;

      try {
        const response = await ec2CodexAPI.allowRdp();
        this.lastAllowedCidr = response.data && response.data.cidr;
        if (response.data && response.data.status) {
          this.status = response.data.status;
        }
      } catch (error) {
        this.error = this.extractError(error);
      } finally {
        this.loadingAllowRdp = false;
      }
    },
    async downloadRdp() {
      this.error = "";
      this.loadingRdp = true;

      try {
        const response = await ec2CodexAPI.downloadRdp();
        const blob = new Blob([response.data], { type: "application/x-rdp" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "codex-ec2-ohio.rdp";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.error = this.extractError(error);
      } finally {
        this.loadingRdp = false;
      }
    },
    async copyHost() {
      if (!this.status.rdpHost) {
        return;
      }

      try {
        await navigator.clipboard.writeText(this.status.rdpHost);
      } catch (error) {
        this.error = "Nao foi possivel copiar automaticamente. Selecione o host na tela.";
      }
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      return new Date(value).toLocaleString("pt-BR");
    },
    extractError(error) {
      const data = error && error.response && error.response.data;
      if (data && data.mensagem) {
        return data.mensagem;
      }

      if (typeof data === "string") {
        return data;
      }

      return "Nao foi possivel executar a operacao.";
    },
  },
};
</script>

<style scoped>
.ec2-codex-page {
  padding: 24px;
}

.page-header {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 20px;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 600;
  line-height: 1.2;
  margin: 0;
}

.page-header p {
  color: #5f6368;
  margin: 6px 0 0;
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.panel-title {
  font-size: 16px;
  font-weight: 600;
}

.state-row {
  align-items: center;
  display: flex;
  gap: 12px;
}

.detail-list {
  display: grid;
  gap: 12px;
}

.detail-list div {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.detail-list span {
  color: #6b7280;
  font-size: 12px;
  text-transform: uppercase;
}

.detail-list strong {
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.action-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.help-text {
  color: #4b5563;
  font-size: 13px;
}

.help-text p {
  margin: 0 0 4px;
}

@media (max-width: 600px) {
  .ec2-codex-page {
    padding: 12px;
  }

  .page-header {
    display: block;
  }

  .header-actions {
    justify-content: stretch;
    margin-top: 12px;
  }

  .header-actions .v-btn,
  .action-grid .v-btn {
    width: 100%;
  }
}
</style>
